import { useState, useEffect } from 'react'
import { EnterEmail } from '@/components/auth/enter-email'
import { EnterOtp } from '@/components/auth/enter-otp'
import { LocationStepComponent } from '@/pages/logged-out/signup/location-step'
import { IntroductionStepComponent } from '@/pages/logged-out/signup/introduction-step'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useSessionContext } from '@/state/session-context'

export default function SignupPanel() {
	const reactLocation = useLocation()
	const navigate = useNavigate()
	const { refreshSession } = useSessionContext()
	
	const [currentStep, setCurrentStep] = useState(1)
	const [email, setEmail] = useState('')
	const [otpChallengeId, setOtpChallengeId] = useState('')
	const [location, setLocation] = useState({ lat: 0, lng: 0 })
	const [hasValidLocation, setHasValidLocation] = useState(false)
	const [name, setName] = useState('')
	const [introduction, setIntroduction] = useState('')
	const [areaHasBot, setAreaHasBot] = useState<boolean | undefined>(undefined)
	const [areaUserCount, setAreaUserCount] = useState(0)

	useEffect(() => {
		if (reactLocation.hash) {
			const element = document.querySelector(reactLocation.hash)
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' })
			}
		}
	}, [reactLocation.hash])	

	const steps = [
		{ number: 1, title: 'Enter your email address' },
		{ number: 2, title: 'Enter email verification code' },
		{ number: 3, title: 'Pick your area' },
		{ number: 4, title: 'Optional: Introduce yourself' },
	]

	const renderStep = () => {
		switch (currentStep) {
			case 1:
				return <EnterEmail 
					mode="signup"
					email={email} 
					setEmail={setEmail} 
					onContinue={() => setCurrentStep(2)}
					setOtpChallengeId={setOtpChallengeId}
				/>
			case 2:
				return <EnterOtp
					mode="signup"
					email={email}
					otpChallengeId={otpChallengeId}
					onContinue={async (isExistingUser) => {
						if (isExistingUser) {
							//	user already exists, treat it as a successful login.
							//	refresh first so the React session state catches up with the
							//	auth cookie set by verify-otp — otherwise guarded routes see
							//	a stale null session and bounce the user back to landing.
							await refreshSession()
							navigate('/chat')
						} else {
							//	new user, continue with the signup flow
							setCurrentStep(3)
						}
					}}
					onBack={() => {
						setOtpChallengeId('')
						setCurrentStep(1)
					}}
				/>
			case 3:
				return <LocationStepComponent 
					location={location} 
					setLocation={setLocation} 
					hasValidLocation={hasValidLocation}
					setHasValidLocation={setHasValidLocation}
					setCurrentStep={setCurrentStep} 
					areaHasBot={areaHasBot}
					setAreaHasBot={setAreaHasBot}
					areaUserCount={areaUserCount}
					setAreaUserCount={setAreaUserCount}
				/>
			case 4:
				return <IntroductionStepComponent 
					email={email}
					location={location}
					name={name}
					setName={setName}
					introduction={introduction}
					setIntroduction={setIntroduction}
					setCurrentStep={setCurrentStep}
				/>
			default:
				return null
		}
	}

	return (
		<div id="signup" className="space-y-4">
			<div className="text-sm text-gray-500 flex justify-center">
				Already have an account?
				<Link to="/login" className="text-rose-500 hover:text-rose-700 ml-2">
					Sign in here
				</Link>
			</div>

			{steps.map((step) => (
				<div
					key={step.number}
					// className={`p-4 border rounded-md bg-[#FCFDFF] ${
					className={`p-4 border rounded-md bg-[#FFFFFF] ${
						currentStep === step.number 
							? 'border-rose-600 shadow-md' 
							: 'border-gray-200'
					}`}
				>
					<h3 className="font-semibold mb-4">
						{step.number}. {step.title}
					</h3>
					{currentStep === step.number && (
						<div className="mt-6">
							{renderStep()}
						</div>
					)}
				</div>
			))}
		</div>
	)
}
